import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, PushPin, Heart, X } from "@phosphor-icons/react";
import { STATUS_CONFIG, STATUS_ORDER, CONTENT_TYPES, PLATFORMS } from "@/lib/generateConstants";

export function HistoryFilters({
  search, onSearchChange,
  filterType, onFilterTypeChange,
  filterPlatform, onFilterPlatformChange,
  filterMode, onFilterModeChange,
  filterStatus, onFilterStatusChange,
}) {
  return (
    <>
      {/* Search & dropdowns */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search topics, titles, or content..."
            className="pl-10 bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
            data-testid="history-search-input"
          />
        </div>
        <div className="flex gap-2">
          <Select value={filterType || "all"} onValueChange={(v) => onFilterTypeChange(v === "all" ? "" : v)}>
            <SelectTrigger className="flex-1 sm:w-[160px] bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] h-11" data-testid="history-filter-type">
              <SelectValue placeholder="Content Type" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              <SelectItem value="all" className="text-[var(--text-secondary)]">All Types</SelectItem>
              {CONTENT_TYPES.map((t) => (
                <SelectItem key={t} value={t} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPlatform || "all"} onValueChange={(v) => onFilterPlatformChange(v === "all" ? "" : v)}>
            <SelectTrigger className="flex-1 sm:w-[140px] bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] h-11" data-testid="history-filter-platform">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              <SelectItem value="all" className="text-[var(--text-secondary)]">All Platforms</SelectItem>
              {PLATFORMS.map((p) => (
                <SelectItem key={p} value={p} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Quick filter pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => onFilterModeChange(filterMode === "pinned" ? "" : "pinned")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
            filterMode === "pinned" ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
          }`}
          data-testid="filter-pinned-button"
        >
          <PushPin size={12} weight={filterMode === "pinned" ? "fill" : "regular"} />Pinned
        </button>
        <button
          onClick={() => onFilterModeChange(filterMode === "favorites" ? "" : "favorites")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
            filterMode === "favorites" ? "bg-rose-500/10 border-rose-500 text-rose-400" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
          }`}
          data-testid="filter-favorites-button"
        >
          <Heart size={12} weight={filterMode === "favorites" ? "fill" : "regular"} />Favorites
        </button>
        {filterMode && (
          <button onClick={() => onFilterModeChange("")} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="clear-filter-button">
            <X size={12} />Clear
          </button>
        )}
      </div>

      {/* Status filter bar */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filter-bar">
        {[{ key: "", label: "All" }, ...STATUS_ORDER.map((s) => ({ key: s, label: STATUS_CONFIG[s].label }))].map((f) => (
          <button
            key={f.key}
            onClick={() => onFilterStatusChange(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
              filterStatus === f.key ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
            }`}
            data-testid={`filter-status-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </>
  );
}
